'use client';

import {
  buildResultsPerPage,
  loadPaginationActions,
} from '@coveo/atomic-react';
import { DEFAULT_SEARCH_PAGE_SIZE } from '@lib/constants/coveo';
import { useFlags } from '@lib/flags/amplitude/client';
import { Flags } from '@lib/flags/flagConstants';
import { useController } from '@lib/hooks/useController';
import { useMemo, useState } from 'react';
import { useEngine } from '../EngineContext';
import { resolveFlagPageSize } from '../utils/flagPageSize';
import { isPageSizeOption, parseUrlPageSize } from '../utils/pageSize';
import {
  getResultsPerPageFromLocalStorage,
  saveResultsPerPagePreference,
} from '../utils/resultsPerPageStorage';
import { ResultsPerPageProps } from './ResultsPerPage.types';
import { ResultsPerPageControl } from './ResultsPerPageControl';

export const ResultsPerPage = ({ className }: ResultsPerPageProps) => {
  const { flags } = useFlags();
  const flag = flags[Flags.INCREASE_DEFAULT_RESULTS_PER_PAGE];
  const defaultPageSize =
    resolveFlagPageSize(flag?.payload) ?? DEFAULT_SEARCH_PAGE_SIZE;

  // Compute the initial value once on mount to prevent controller re-creation.
  // Re-creating the controller dispatches registerNumberOfResults which sets
  // defaultNumberOfResults = numberOfResults, causing Coveo to omit
  // numberOfResults from the URL hash (it drops values equal to the default).
  // URL hash takes priority so that legacy redirect URLs (e.g. rows=96 → #numberOfResults=96)
  // and custom URL sizes (e.g. #numberOfResults=150) survive the useEngineState
  // ready→loading→ready remount cycle. Only a standard option is taken from
  // localStorage, so a hand-edited store can't seed a custom size.
  const [initialNumberOfResults] = useState(() => {
    const hash = window.location.hash.slice(1);
    const fromUrl = parseUrlPageSize(
      new URLSearchParams(hash).get('numberOfResults')
    );
    if (fromUrl !== undefined) return fromUrl;
    const saved = getResultsPerPageFromLocalStorage();
    return saved !== undefined && isPageSizeOption(saved)
      ? saved
      : defaultPageSize;
  });

  const eng = useEngine();
  const controller = useMemo(
    () =>
      buildResultsPerPage(eng, {
        initialState: { numberOfResults: initialNumberOfResults },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eng]
  );
  const state = useController(controller);

  const handleSelect = (numberOfResults: number) => {
    saveResultsPerPagePreference(numberOfResults);
    if (numberOfResults === state.numberOfResults) return;
    controller.set(numberOfResults);
    // Update defaultNumberOfResults so SPA hash-change navigation doesn't revert the
    // setting via restoreSearchParameters. registerNumberOfResults is a pure Redux
    // state action — it does NOT dispatch executeSearch, so no second search fires.
    // Trade-off: once the default equals the user's choice, Coveo omits numberOfResults
    // from the URL hash, so the preference is persisted via localStorage only and won't
    // survive copy-pasted or shared URLs. Page-size is treated as a personal preference
    // rather than a shareable filter parameter.
    eng.dispatch(
      loadPaginationActions(eng).registerNumberOfResults(numberOfResults)
    );
  };

  return (
    <ResultsPerPageControl
      numberOfResults={state.numberOfResults}
      onSelect={handleSelect}
      className={className}
    />
  );
};
